import CheckmarkIcon from '@assets/icons/checkmark.svg';
import React from 'react';
import { createStyles } from '@/contexts/theme-context';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { Props as ListItemProps, ListOption } from './list-item';
import { MaybeScrollingList } from './maybe-scrolling-list';
import { StyleProp, Text, View, ViewStyle } from 'react-native';

export type FilterOption = ListOption<{
  action: () => void;
  count?: number;
  isSelected?: boolean;
}>;

interface Props {
  contentContainerStyle?: StyleProp<ViewStyle>;
  data: FilterOption[];
  scrollEnabled?: boolean;
}

export const SplitFilterList: React.FC<Props> = ({
  contentContainerStyle,
  data,
  scrollEnabled,
}) => {
  const [styles, theme] = useStyles();

  const sharedProps: Partial<ListItemProps<FilterOption>> = {
    listItemStyle: ({ metadata }) => [
      styles.listItem,
      metadata?.isSelected && styles.selectedListItem,
    ],
    onSelect: ({ metadata }) => {
      metadata?.action();
    },
    renderMainContent: ({ title, metadata }) => {
      return (
        <View style={styles.row}>
          <Text
            numberOfLines={1}
            style={[TYPOGRAPHY.medium, FONTS.primarySemiBold]}
          >
            {title}
          </Text>
          {typeof metadata?.count === 'number' && metadata.count > 0 ? (
            <View
              style={[
                styles.count,
                metadata?.isSelected ? styles.countSelected : null,
              ]}
            >
              <Text
                style={[
                  TYPOGRAPHY.xsmall,
                  FONTS.primarySemiBold,
                  styles.countText,
                ]}
              >
                {metadata.count}
              </Text>
            </View>
          ) : null}
        </View>
      );
    },
    renderRightComponent: ({ metadata }) => {
      if (metadata?.isSelected) {
        return (
          <CheckmarkIcon height={20} stroke={theme.colors.accent} width={20} />
        );
      }
      return null;
    },
  };

  return (
    <MaybeScrollingList<FilterOption>
      data={data}
      listProps={{
        ItemSeparatorComponent: null,
      }}
      scrollEnabled={scrollEnabled}
      sharedProps={sharedProps}
      style={contentContainerStyle}
    />
  );
};

const useStyles = createStyles((theme) => ({
  count: {
    alignItems: 'center',
    backgroundColor: theme.sk.color.bg.tertiary.default,
    borderRadius: 16,
    height: 16,
    justifyContent: 'center',
    marginLeft: 4,
    width: 16,
  },
  countSelected: {
    backgroundColor: theme.sk.color.text.ondark,
  },
  countText: {
    color: theme.sk.color.text.secondary,
  },
  listItem: {
    borderRadius: 8,
    marginBottom: 4,
    paddingHorizontal: 8,
    paddingVertical: 10,
  },
  row: {
    alignItems: 'center',
    flex: 1,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  selectedListItem: {
    backgroundColor: theme.colors.background,
  },
}));
